import { ERRORS_TRIM_FLOOR } from "./config";
import { byteLengthOf } from "./framing";
import type { ErrTuple, SnapshotSD3 } from "./snapshot";

// 予算超過時の間引き。価値の低い順に削る:
//   1. エラーの stackHead を落とす (時刻/name/message は残す)
//   2. 古い日次タプルを落とす (直近を優先)
//   3. 最終手段: エラー件数を下限まで減らす (古い方から)
// v2 と違い recentEvents はそもそもQRに載せないので間引き対象に存在しない。

export interface TrimReport {
  trimmed: boolean;
  strippedStacks: boolean;
  droppedDays: number;
  droppedErrors: number;
}

export interface TrimResult {
  snapshot: SnapshotSD3;
  json: string;
  byteSize: number;
  report: TrimReport;
}

const NO_TRIM: TrimReport = {
  trimmed: false,
  strippedStacks: false,
  droppedDays: 0,
  droppedErrors: 0,
};

export const trimToBudget = (snap: SnapshotSD3, maxBytes: number): TrimResult => {
  let json = JSON.stringify(snap);
  if (byteLengthOf(json) <= maxBytes) {
    return { snapshot: snap, json, byteSize: byteLengthOf(json), report: NO_TRIM };
  }

  // 元を変更しない可変コピー。
  const s: SnapshotSD3 = {
    ...snap,
    u: { cols: snap.u.cols, days: [...snap.u.days] },
    e: snap.e.map((t) => [...t] as ErrTuple),
  };
  const size = (): number => byteLengthOf(JSON.stringify(s));

  let strippedStacks = false;
  let droppedDays = 0;
  let droppedErrors = 0;

  // 1. stackHead 除去。
  if (size() > maxBytes) {
    s.e = s.e.map((t) => {
      if (t.length === 4) {
        strippedStacks = true;
        return [t[0], t[1], t[2]] as ErrTuple;
      }
      return t;
    });
  }
  // 2. 古い日次から削る。
  while (size() > maxBytes && s.u.days.length > 0) {
    s.u.days.shift();
    droppedDays += 1;
  }
  // 3. 最終手段: エラー件数を下限まで。
  while (size() > maxBytes && s.e.length > ERRORS_TRIM_FLOOR) {
    s.e.shift();
    droppedErrors += 1;
  }

  json = JSON.stringify(s);
  return {
    snapshot: s,
    json,
    byteSize: byteLengthOf(json),
    report: {
      trimmed: strippedStacks || droppedDays > 0 || droppedErrors > 0,
      strippedStacks,
      droppedDays,
      droppedErrors,
    },
  };
};
